/**
 * Earring Details Updater
 * Updates earring-specific metafield details when variant changes.
 * Pattern is based on bracelet-details.js.
 */

(function() {
  'use strict';

  // Wait for Shopify's utilities to be available
  function waitForShopifyUtils(callback) {
    const hasSubscribe =
      (typeof subscribe !== 'undefined' && typeof subscribe === 'function') ||
      (window.subscribe && typeof window.subscribe === 'function');

    const hasPubSub =
      typeof PUB_SUB_EVENTS !== 'undefined' ||
      window.PUB_SUB_EVENTS;

    if (hasSubscribe && hasPubSub) {
      callback();
    } else {
      setTimeout(() => waitForShopifyUtils(callback), 100);
    }
  }

  function initializeEarringDetails() {
    const earringsDataScripts = document.querySelectorAll('script[id^="earrings-data-"]');

    earringsDataScripts.forEach((script) => {
      const sectionId = script.id.replace('earrings-data-', '');

      if (script.dataset.initialized === 'true') return;
      script.dataset.initialized = 'true';

      try {
        const earringsData = JSON.parse(script.textContent);
        const updater = new EarringDetailsUpdater(sectionId, earringsData);
        updater.init();
      } catch (e) {
        console.error('Error parsing earrings data:', e);
      }
    });
  }

  class EarringDetailsUpdater {
    constructor(sectionId, earringsData) {
      this.sectionId = sectionId;
      this.earringsData = earringsData;
      this.variantChangeUnsubscriber = null;
      this.directClickHandler = null;
      this.directChangeHandler = null;
    }

    init() {
      waitForShopifyUtils(() => {
        try {
          const subscribeFn =
            (typeof subscribe !== 'undefined' && typeof subscribe === 'function')
              ? subscribe
              : (window.subscribe && typeof window.subscribe === 'function' ? window.subscribe : null);

          const pubSubEvents =
            typeof PUB_SUB_EVENTS !== 'undefined'
              ? PUB_SUB_EVENTS
              : (window.PUB_SUB_EVENTS || null);

          if (subscribeFn && pubSubEvents) {
            this.variantChangeUnsubscriber = subscribeFn(
              pubSubEvents.variantChange,
              (event) => {
                if (!event || !event.data) return;

                const variant = event.data.variant;
                const sectionId = event.data.sectionId;

                if (sectionId && sectionId !== this.sectionId) return;

                if (variant) {
                  this.updateDetails(variant);
                }
              }
            );
          }
        } catch (e) {
          console.error('Error subscribing to variant change for earrings:', e);
        }
      });

      this.setupDirectListeners();

      setTimeout(() => {
        this.updateFromCurrentSelection();
      }, 500);
    }

    setupDirectListeners() {
      const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!variantSelects) {
        setTimeout(() => this.setupDirectListeners(), 500);
        return;
      }

      this.directClickHandler = (e) => {
        const variantOption = e.target.closest(
          'input[type="radio"], select, [data-option-value], .variant-picker__option, button[data-value]'
        );
        if (variantOption) {
          setTimeout(() => {
            this.updateFromCurrentSelection();
          }, 300);
        }
      };

      this.directChangeHandler = () => {
        setTimeout(() => {
          this.updateFromCurrentSelection();
        }, 300);
      };

      variantSelects.addEventListener('click', this.directClickHandler, true);
      variantSelects.addEventListener('change', this.directChangeHandler, true);
    }

    updateFromCurrentSelection() {
      const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (!variantSelects) return;

      const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
      if (selectedVariantScript) {
        try {
          const variant = JSON.parse(selectedVariantScript.textContent);
          this.updateDetails(variant);
          return;
        } catch (e) {
          // fall through to fallback method
        }
      }

      const selectedInputs = variantSelects.querySelectorAll('input[type="radio"]:checked, select');
      if (selectedInputs.length > 0) {
        const selectedValues = Array.from(selectedInputs)
          .map((input) => {
            if (input.type === 'radio') {
              return input.value;
            } else if (input.tagName === 'SELECT') {
              return input.options[input.selectedIndex]?.value;
            }
            return null;
          })
          .filter((v) => v !== null);

        const variantData = this.earringsData.variants.find((v) => {
          return selectedValues.some((val) => {
            if (v.option1 === val) return true;
            if (val.includes(v.option1) || v.option1.includes(val)) return true;
            return false;
          });
        });

        if (variantData) {
          this.updateByPosition(variantData.position);
        }
      }
    }

    updateDetails(variant) {
      if (!variant || !this.earringsData) return;

      const postThicknessElement = document.getElementById(`post-thickness-${this.sectionId}`);
      const postLengthElement = document.getElementById(`post-length-${this.sectionId}`);
      const originElement = document.getElementById(`origin-${this.sectionId}`);

      const variantId = variant.id || variant.variant_id;

      const variantData = this.earringsData.variants.find((v) => {
        const vId = v.id.toString();
        const compareId = variantId.toString();
        return (
          vId === compareId ||
          vId.endsWith(compareId) ||
          compareId.endsWith(vId) ||
          vId.includes(compareId) ||
          compareId.includes(vId)
        );
      });

      if (variantData) {
        const position = variantData.position;

        if (
          postThicknessElement &&
          this.earringsData.postThicknessValues &&
          position >= 0 &&
          position < this.earringsData.postThicknessValues.length
        ) {
          const value = this.earringsData.postThicknessValues[position].trim();
          postThicknessElement.textContent = value;
        }

        if (
          postLengthElement &&
          this.earringsData.postLengthValues &&
          position >= 0 &&
          position < this.earringsData.postLengthValues.length
        ) {
          const value = this.earringsData.postLengthValues[position].trim();
          postLengthElement.textContent = value;
        }

        if (
          originElement &&
          this.earringsData.originValues &&
          position >= 0 &&
          position < this.earringsData.originValues.length
        ) {
          const value = this.earringsData.originValues[position].trim();
          originElement.textContent = value;
        }
      }
    }

    updateByPosition(position) {
      if (position < 0) return;

      const postThicknessElement = document.getElementById(`post-thickness-${this.sectionId}`);
      const postLengthElement = document.getElementById(`post-length-${this.sectionId}`);
      const originElement = document.getElementById(`origin-${this.sectionId}`);

      if (
        postThicknessElement &&
        this.earringsData.postThicknessValues &&
        position < this.earringsData.postThicknessValues.length
      ) {
        const value = this.earringsData.postThicknessValues[position].trim();
        postThicknessElement.textContent = value;
      }

      if (
        postLengthElement &&
        this.earringsData.postLengthValues &&
        position < this.earringsData.postLengthValues.length
      ) {
        const value = this.earringsData.postLengthValues[position].trim();
        postLengthElement.textContent = value;
      }

      if (
        originElement &&
        this.earringsData.originValues &&
        position < this.earringsData.originValues.length
      ) {
        const value = this.earringsData.originValues[position].trim();
        originElement.textContent = value;
      }
    }

    destroy() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (variantSelects) {
        if (this.directClickHandler) {
          variantSelects.removeEventListener('click', this.directClickHandler, true);
        }
        if (this.directChangeHandler) {
          variantSelects.removeEventListener('change', this.directChangeHandler, true);
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeEarringDetails);
  } else {
    initializeEarringDetails();
  }

  waitForShopifyUtils(() => {
    setTimeout(initializeEarringDetails, 200);
  });
})();


